import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getAdminId } from '../utils/getAdminId';
import type { IssueEmailLog, IssueEmailLogDetail } from '../types/orderIssues';

export function useIssueEmailHistory() {
  const { user } = useAuth();
  const adminId = getAdminId(user);

  const [logs, setLogs] = useState<IssueEmailLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);

  const [detail, setDetail] = useState<IssueEmailLogDetail[] | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadLogs = useCallback(async () => {
    if (!adminId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_issue_email_logs', {
        p_admin_id: adminId,
        p_limit: pageSize,
        p_offset: page * pageSize,
      });

      if (error) throw error;
      const rows = (data || []) as IssueEmailLog[];
      setLogs(rows);
      setTotal(rows.length > 0 ? Number(rows[0].total_count) || 0 : 0);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [adminId, page, pageSize]);

  const loadLogDetail = useCallback(
    async (logId: string) => {
      if (!adminId) return;
      setDetailLoading(true);
      setDetail(null);
      try {
        const { data, error } = await supabase.rpc('get_issue_email_log_detail', {
          p_admin_id: adminId,
          p_log_id: logId,
        });
        if (error) throw error;
        setDetail((data || []) as IssueEmailLogDetail[]);
      } catch {
        setDetail([]);
      } finally {
        setDetailLoading(false);
      }
    },
    [adminId]
  );

  const clearDetail = useCallback(() => {
    setDetail(null);
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return {
    logs,
    loading,
    total,
    page,
    pageSize,
    setPage,
    detail,
    detailLoading,
    loadLogDetail,
    clearDetail,
    loadLogs,
  };
}
